import React, { Component } from 'react'
import PropTypes from 'prop-types'
import omit from 'lodash/fp/omit'
import wrappedDisplayName from '../../utils/wrapperDisplayName'
import getInjectors from '../utils/sagaInjector'
import injectStore from './injectStore'

const omitStore = omit('store')

/**
 * Dinamically injects saga to store
 *
 * @param {Object} options - options to inject saga
 * @param {String} options.key - key to inject saga
 * @param {Function} options.saga - saga function to run
 * @param {String} [options.mode=DAEMON] - mode of inject saga and eject saga.
 *   - constants.DAEMON - the saga starts on component mount and never canceled
 *   - constants.ONCE_TILL_UNMOUNT - the saga starts on component mount and cancels
 *   when is unmounted. The saga never run again
 * @return {Function} to create withSagaComponent
 */
export default function injectSaga({ key, saga, mode }) {
  /**
   * Create withSaga component, passes component props as saga args
   *
   * @param {Component|StatelessFunction} WrappedComponent - react component to wrap
   * @return {Component} WithSagaComponent
   */
  return WrappedComponent => {
    class InjectSaga extends Component {
      static displayName = wrappedDisplayName('withSaga', WrappedComponent)

      static propTypes = {
        store: PropTypes.shape({
          runSaga: PropTypes.func.isRequired,
          injectedSagas: PropTypes.object.isRequired
        }).isRequired
      }

      constructor(props) {
        super(props)

        this.injectors = getInjectors(props.store)
        this.injectors.injectSaga(key, { saga, mode }, omitStore(props))
      }

      componentWillUnmount() {
        this.injectors.ejectSaga(key)
      }

      render() {
        return <WrappedComponent {...omitStore(this.props)} />
      }
    }

    return injectStore(InjectSaga)
  }
}
